import { useCallback, useMemo, useState } from 'react';
import {
  clampColorValue,
  createColorChangeEvent,
  getColorPickerStyle,
  getHsvFromHex,
  hexToRgb,
  hsvToRgb,
  isColorHexDraft,
  normalizeColorHex,
  rgbToHex,
} from './colorPickerUtils';

const CHANNELS = [
  { key: 'r', label: 'R', aria: 'red' },
  { key: 'g', label: 'G', aria: 'green' },
  { key: 'b', label: 'B', aria: 'blue' },
];

function EyeDropperIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m14.5 5.5 4 4" />
      <path d="m5 19 6.5-6.5" />
      <path d="m10 8.5 5.5 5.5" />
      <path d="m3 21 2-2" />
      <path d="m13 7 2.5-2.5a2.1 2.1 0 0 1 3 3L16 10" />
    </svg>
  );
}

export default function ColorPickerPopover({
  value,
  onChange,
  id,
  name,
  ariaLabel = 'Color',
}) {
  const normalizedColor = normalizeColorHex(value);
  const hsv = useMemo(() => getHsvFromHex(normalizedColor), [normalizedColor]);
  const propRgb = useMemo(() => hexToRgb(normalizedColor), [normalizedColor]);
  const [draft, setDraft] = useState(() => ({
    propColor: normalizedColor,
    hex: normalizedColor,
    rgb: propRgb,
  }));
  const activeDraft = draft.propColor === normalizedColor
    ? draft
    : {
        propColor: normalizedColor,
        hex: normalizedColor,
        rgb: propRgb,
      };

  const canUseEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

  const emitColor = useCallback((nextColor) => {
    const normalizedNext = normalizeColorHex(nextColor, normalizedColor);
    setDraft({
      propColor: normalizedColor,
      hex: normalizedNext,
      rgb: hexToRgb(normalizedNext),
    });
    onChange?.(createColorChangeEvent(normalizedNext, { id, name }));
  }, [id, name, normalizedColor, onChange]);

  const handleEyeDropperClick = useCallback(async () => {
    if (!canUseEyeDropper) {
      return;
    }

    try {
      const result = await new window.EyeDropper().open();
      if (result?.sRGBHex) {
        emitColor(result.sRGBHex);
      }
    } catch {
      // User cancelled the native eyedropper.
    }
  }, [canUseEyeDropper, emitColor]);

  const handleRgbChange = useCallback((channel, event) => {
    const nextValue = event.target.value.replace(/\D/g, '');
    const nextDraft = {
      ...activeDraft.rgb,
      [channel]: nextValue,
    };
    setDraft({
      propColor: normalizedColor,
      hex: activeDraft.hex,
      rgb: nextDraft,
    });

    if (nextValue === '') {
      return;
    }

    const parsedValue = Number(nextValue);
    if (!Number.isFinite(parsedValue)) {
      return;
    }

    const nextRgb = CHANNELS.reduce((accumulator, nextChannel) => {
      const channelValue = Number(nextDraft[nextChannel.key]);
      return {
        ...accumulator,
        [nextChannel.key]: Number.isFinite(channelValue)
          ? clampColorValue(channelValue, 0, 255)
          : propRgb[nextChannel.key],
      };
    }, {});
    emitColor(rgbToHex(nextRgb));
  }, [activeDraft.hex, activeDraft.rgb, emitColor, normalizedColor, propRgb]);

  const handleRgbBlur = useCallback(() => {
    setDraft({
      propColor: normalizedColor,
      hex: normalizeColorHex(activeDraft.hex, normalizedColor),
      rgb: hexToRgb(normalizeColorHex(activeDraft.hex, normalizedColor)),
    });
  }, [activeDraft.hex, normalizedColor]);

  const applyPlanePointer = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const nextSaturation = clampColorValue((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
    const nextValue = clampColorValue(1 - ((event.clientY - rect.top) / Math.max(rect.height, 1)), 0, 1);

    emitColor(rgbToHex(hsvToRgb({
      h: hsv.h,
      s: nextSaturation,
      v: nextValue,
    })));
  }, [emitColor, hsv.h]);

  const handlePlanePointerDown = useCallback((event) => {
    applyPlanePointer(event);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [applyPlanePointer]);

  const handlePlanePointerMove = useCallback((event) => {
    if (event.buttons !== 1) {
      return;
    }
    applyPlanePointer(event);
  }, [applyPlanePointer]);

  const handleHueChange = useCallback((event) => {
    emitColor(rgbToHex(hsvToRgb({
      h: Number(event.target.value),
      s: hsv.s,
      v: hsv.v,
    })));
  }, [emitColor, hsv.s, hsv.v]);

  const handleHexChange = useCallback((event) => {
    const nextDraft = event.target.value;
    setDraft({
      propColor: normalizedColor,
      hex: nextDraft,
      rgb: activeDraft.rgb,
    });

    if (isColorHexDraft(nextDraft)) {
      emitColor(normalizeColorHex(nextDraft));
    }
  }, [activeDraft.rgb, emitColor, normalizedColor]);

  const handleHexBlur = useCallback(() => {
    const normalizedDraft = normalizeColorHex(activeDraft.hex, normalizedColor);
    setDraft({
      propColor: normalizedColor,
      hex: normalizedDraft,
      rgb: hexToRgb(normalizedDraft),
    });
  }, [activeDraft.hex, normalizedColor]);

  const hueValue = Math.round(hsv.h);

  return (
    <div
      className="ui-color-picker-popover dropdown-popover-content--compact"
      style={getColorPickerStyle(normalizedColor)}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="ui-color-picker-plane"
        aria-label={`${ariaLabel} saturation and brightness`}
        onPointerDown={handlePlanePointerDown}
        onPointerMove={handlePlanePointerMove}
      >
        <span className="ui-color-picker-plane__marker" />
      </button>

      <div className="ui-color-picker-controls">
        <button
          type="button"
          className="ui-color-picker-eyedropper"
          onClick={handleEyeDropperClick}
          disabled={!canUseEyeDropper}
          aria-label={`${ariaLabel} eyedropper`}
          title={canUseEyeDropper ? 'Pick color from screen' : 'Eyedropper unavailable'}
        >
          <EyeDropperIcon />
        </button>
        <span className="ui-color-picker-preview" aria-hidden="true" />
        <input
          className="ui-color-picker-hue-slider"
          type="range"
          min="0"
          max="360"
          step="1"
          value={hueValue}
          onChange={handleHueChange}
          aria-label={`${ariaLabel} hue`}
        />
      </div>

      <div className="ui-color-picker-rgb-grid">
        {CHANNELS.map((channel) => (
          <label key={channel.key} className="ui-color-picker-channel">
            <span className="ui-color-picker-channel-label" aria-hidden="true">{channel.label}</span>
            <input
              className="ui-text-input ui-text-input--compact ui-text-input--dense ui-color-picker-channel-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={activeDraft.rgb[channel.key]}
              onChange={(event) => handleRgbChange(channel.key, event)}
              onBlur={handleRgbBlur}
              aria-label={`${ariaLabel} ${channel.aria}`}
            />
          </label>
        ))}
      </div>

      <input
        className="ui-text-input ui-text-input--compact ui-text-input--dense ui-color-picker-hex-input"
        value={activeDraft.hex}
        onChange={handleHexChange}
        onBlur={handleHexBlur}
        aria-label={`${ariaLabel} hex`}
        spellCheck={false}
      />
    </div>
  );
}
